import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import Reveal from "@/components/Reveal";
import { buildWhatsAppLink } from "@/lib/constants";

const BELIEFS = [
  {
    n: "01",
    t: "Trust before transaction",
    d: "Every interaction should be supported by identity, proof, and accountability.",
  },
  {
    n: "02",
    t: "Service with dignity",
    d: "Members, workers, agents, riders, and service providers must be respected as contributors.",
  },
  {
    n: "03",
    t: "Governance before growth",
    d: "Expansion must happen through clear rules, records, and responsibility.",
  },
  {
    n: "04",
    t: "No hidden privilege",
    d: "Participation should be fair, transparent, and protected from nepotism or unfair advantage.",
  },
  {
    n: "05",
    t: "Evidence-based action",
    d: "Work, claims, services and decisions must be supported by records and proof.",
  },
  {
    n: "06",
    t: "Community-led prosperity",
    d: "Value should circulate back into the people, places, and systems that create it.",
  },
  {
    n: "07",
    t: "Balance & responsibility",
    d: "Economic activity must be aligned with ethics, fairness, and long-term stability.",
  },
  {
    n: "08",
    t: "Learning & opportunity",
    d: "Youth and new participants should have clear pathways into work, skill-building, and contribution.",
  },
];

const ACTIVITIES = [
  {
    t: "Verified Member Network",
    d: "A trusted registry of members, service providers, professionals, businesses, institutions, agents, riders, and community participants.",
  },
  {
    t: "Service & Work Marketplace",
    d: "Members can offer and access services through transparent rules, pricing bands, identity verification, milestone-based work, and dispute-safe engagement.",
  },
  {
    t: "Community Governance & Dispute Resolution",
    d: "Operating rules, member responsibilities, escalation paths, mediation, and governance frameworks for fair participation.",
  },
  {
    t: "Youth, Internship & Skill Pathways",
    d: "Connecting youth, apprentices, interns, and learners to real work, workshops, factories, mentors, and practical projects.",
  },
  {
    t: "Physical & Digital Commons Infrastructure",
    d: "Connects physical hubs, workshops, local nodes, factories, and service centres into one trusted operating environment.",
  },
];

const MILESTONES = [
  {
    n: "01",
    t: "Concept framework drafted",
    d: "Believers Common, Virtual Silk Road, DigitalMe, EmpireOS, RiverOS, and Warden governance frameworks scoped.",
  },
  {
    n: "02",
    t: "Initial service models identified",
    d: "Trusted business-call services, member-led marketplace, workshop/studio hub, internship pathways, material passport systems, and community commerce rails.",
  },
  {
    n: "03",
    t: "Governance principles drafted",
    d: "Member participation, fair pricing, no-family privilege rules, dispute handling, evidence records, and role-based accountability.",
  },
  {
    n: "04",
    t: "Physical hub concept under development",
    d: "Workshop, studio, identity hub, and community meeting point being prepared as the first node.",
  },
  {
    n: "05",
    t: "Program architecture prepared",
    d: "Pilot tracks across business services, manufacturing, civic work, education, and local economic participation.",
  },
];

const About = () => {
  return (
    <div className="bc-fade-up">
      <AboutHero />
      <FoundingStory />
      <Beliefs />
      <Activities />
      <Differentiator />
      <Milestones />
      <FounderNote />
      <CTA />
    </div>
  );
};

const AboutHero = () => (
  <section
    className="relative pt-12 md:pt-16 pb-20 md:pb-32 px-5 sm:px-6 lg:px-10 overflow-hidden"
    data-testid="about-hero"
  >
    <div className="absolute inset-0 bc-grid bc-grid-fade opacity-40" />
    <div className="relative max-w-[1400px] mx-auto grid grid-cols-12 gap-6">
      <div className="col-span-12 md:col-span-9">
        <Reveal>
          <div className="flex items-center gap-3 mb-8">
            <span className="inline-block w-8 h-px bg-[#D6B25E]" />
            <span className="eyebrow">About · The Mandate</span>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <h1 className="font-serif-display text-[40px] sm:text-6xl md:text-7xl lg:text-[96px] leading-[1.04] tracking-tight break-words">
            Rebuilding trust
            <br />
            as <span className="italic text-[#D6B25E]">infrastructure</span>.
          </h1>
        </Reveal>
        <Reveal delay={240}>
          <p className="mt-10 text-[#6F7787] text-base md:text-lg leading-relaxed max-w-2xl">
            Believers Common is designed as both a physical and digital
            community infrastructure — supporting verified participation,
            responsible service delivery, transparent rules, dispute resolution,
            and value circulation through trusted members and accountable
            systems.
          </p>
        </Reveal>
        <Reveal delay={360}>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#story"
              className="btn-ghost"
              data-testid="about-cta-story"
            >
              Read the founding story
              <ArrowDown className="w-4 h-4" strokeWidth={1.6} />
            </a>
            <Link to="/#join" className="btn-primary" data-testid="about-cta-join">
              Register your interest
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
            </Link>
          </div>
        </Reveal>
      </div>
      <div className="hidden md:flex col-span-3 justify-end items-end">
        <Reveal delay={300}>
          <div className="border border-white/10 px-6 py-5 backdrop-blur bg-[#0F1320]/40">
            <div className="eyebrow-slate mb-2">Origin</div>
            <div className="font-serif-display italic text-[#D6B25E] text-2xl">
              Karnataka, India
            </div>
            <div className="bc-divider my-3" />
            <div className="text-xs text-[#6F7787]">
              A physical–digital commons being prepared as a first node.
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const FoundingStory = () => (
  <section
    id="story"
    className="relative py-20 md:py-28 px-5 sm:px-6 lg:px-10 border-t border-white/5"
    data-testid="about-story"
  >
    <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-10">
      <div className="col-span-12 md:col-span-3">
        <Reveal>
          <div className="eyebrow mb-3">01 · Founding story</div>
          <div className="font-serif-display italic text-[#D6B25E] text-3xl">
            How it began.
          </div>
        </Reveal>
      </div>
      <div className="col-span-12 md:col-span-9 space-y-8">
        <Reveal>
          <p className="font-serif-display text-3xl md:text-4xl leading-[1.18] text-[#F6F7FB]">
            Believers Common began as a response to a simple but urgent
            question — <span className="italic text-[#D6B25E]">how can people work together safely, fairly, and meaningfully when trust has become difficult to maintain?</span>
          </p>
        </Reveal>
        <Reveal delay={120}>
          <p className="text-[#6F7787] text-lg leading-relaxed max-w-3xl">
            The idea was formed around the need for a trusted common ground —
            a place where individuals, families, businesses, professionals,
            youth, service providers, and institutions can participate without
            exploitation, confusion, or hidden privilege. It brings together
            identity, governance, service, economic activity, documentation,
            and accountability into one structured ecosystem.
          </p>
        </Reveal>
        <Reveal delay={220}>
          <p className="text-[#6F7787] text-lg leading-relaxed max-w-3xl">
            Believers Common is designed as both a physical and digital
            community infrastructure. It supports verified participation,
            responsible service delivery, transparent rules, dispute
            resolution, and value circulation through trusted members and
            accountable systems.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const Beliefs = () => (
  <section
    className="relative py-20 md:py-28 px-5 sm:px-6 lg:px-10 border-t border-white/5"
    data-testid="about-beliefs"
  >
    <div className="max-w-[1400px] mx-auto">
      <div className="grid grid-cols-12 gap-6 items-end mb-14">
        <div className="col-span-12 md:col-span-7">
          <Reveal>
            <div className="eyebrow mb-4">02 · Founding beliefs</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.04]">
              Eight principles.
              <br />
              <span className="italic text-[#58D2C7]">One mandate.</span>
            </h2>
          </Reveal>
        </div>
        <div className="col-span-12 md:col-span-5">
          <Reveal delay={120}>
            <p className="text-[#6F7787] text-base md:text-lg leading-relaxed">
              The principles below define how members, services, governance,
              and prosperity are organised inside the commons.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
        {BELIEFS.map((b, i) => (
          <Reveal key={b.n} delay={i * 60}>
            <div className="bg-[#0A0D14] p-7 h-full hover:bg-[#0F1320] transition-colors">
              <div className="font-serif-display text-4xl text-[#6F7787]/40 mb-4">
                {b.n}
              </div>
              <h3 className="font-serif-display text-2xl mb-3 leading-tight">
                {b.t}
              </h3>
              <p className="text-[#6F7787] text-sm leading-relaxed">{b.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Activities = () => (
  <section
    className="relative py-20 md:py-28 px-5 sm:px-6 lg:px-10 border-t border-white/5"
    data-testid="about-activities"
  >
    <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-10">
      <div className="col-span-12 md:col-span-4">
        <Reveal>
          <div className="eyebrow mb-4">03 · What we do</div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.04]">
            Five tracks
            <br /> of <span className="italic text-[#D6B25E]">contribution</span>.
          </h2>
        </Reveal>
      </div>
      <div className="col-span-12 md:col-span-8 space-y-px bg-white/5 border border-white/5">
        {ACTIVITIES.map((a, i) => (
          <Reveal key={a.t} delay={i * 80}>
            <div className="bg-[#0A0D14] p-8 grid grid-cols-12 gap-6 items-start hover:bg-[#0F1320] transition-colors">
              <div className="col-span-2 font-serif-display italic text-[#D6B25E] text-3xl">
                0{i + 1}
              </div>
              <div className="col-span-10">
                <h3 className="font-serif-display text-2xl md:text-3xl mb-2">
                  {a.t}
                </h3>
                <p className="text-[#6F7787] leading-relaxed max-w-3xl">
                  {a.d}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Differentiator = () => (
  <section
    className="relative py-20 md:py-28 px-5 sm:px-6 lg:px-10 border-t border-white/5"
    style={{
      background:
        "radial-gradient(ellipse at top right, rgba(31,122,90,0.18), transparent 60%), #0A0D14",
    }}
    data-testid="about-differentiator"
  >
    <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-10">
      <div className="col-span-12 md:col-span-6">
        <Reveal>
          <div className="eyebrow mb-4" style={{ color: "#58D2C7" }}>
            04 · What makes it different
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.04]">
            Not a website.
            <br />
            Not an NGO.
            <br />A{" "}
            <span className="italic text-[#1F7A5A]">governed trust infrastructure.</span>
          </h2>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-8 text-[#6F7787] text-lg leading-relaxed max-w-md">
            Instead of only connecting people, Believers Common creates the
            rules, records, trust, and operating structure required for people
            to work together safely.
          </p>
        </Reveal>
      </div>
      <div className="col-span-12 md:col-span-6">
        <ul className="space-y-px bg-white/5 border border-white/5">
          {[
            "Physical community hubs.",
            "Digital identity and member records.",
            "Verified service participation.",
            "Evidence-based work tracking.",
            "Transparent pricing and contribution rules.",
            "Governance, mediation, and dispute handling.",
            "Youth and professional opportunity pathways.",
            "Ethical economic circulation through the community.",
          ].map((item, i) => (
            <Reveal
              key={item}
              delay={i * 60}
              className="bg-[#0A0D14] p-5 flex items-center gap-5 hover:bg-[#0F1320] transition-colors"
            >
              <span className="font-serif-display italic text-[#D6B25E] w-8">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-[#F6F7FB] text-lg font-serif-display">
                {item}
              </span>
            </Reveal>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const Milestones = () => (
  <section
    className="relative py-20 md:py-28 px-5 sm:px-6 lg:px-10 border-t border-white/5"
    data-testid="about-milestones"
  >
    <div className="max-w-[1400px] mx-auto">
      <div className="grid grid-cols-12 gap-6 items-end mb-14">
        <div className="col-span-12 md:col-span-7">
          <Reveal>
            <div className="eyebrow mb-4">05 · Milestones</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.04]">
              Where we are <span className="italic text-[#D6B25E]">today</span>.
            </h2>
          </Reveal>
        </div>
        <div className="col-span-12 md:col-span-5">
          <Reveal delay={120}>
            <p className="text-[#6F7787] leading-relaxed">
              Believers Common is in its founding phase. The frameworks below
              are being shaped into working systems through pilot programs.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="relative grid grid-cols-1 md:grid-cols-5 gap-px bg-white/5 border border-white/5">
        {MILESTONES.map((m, i) => (
          <Reveal key={m.n} delay={i * 80}>
            <div className="bg-[#0A0D14] p-6 h-full flex flex-col">
              <div className="flex items-center gap-2 mb-6">
                <span className="w-2 h-2 rounded-full bg-[#D6B25E]" />
                <span className="text-[10px] tracking-[0.32em] uppercase text-[#6F7787]">
                  Phase {m.n}
                </span>
              </div>
              <h3 className="font-serif-display text-xl leading-tight mb-3">
                {m.t}
              </h3>
              <p className="text-[#6F7787] text-sm leading-relaxed mt-auto">
                {m.d}
              </p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-px bg-white/5 border border-white/5">
        {[
          ["1", "Founding mission"],
          ["1", "Physical-digital commons model"],
          ["5", "Core participation groups"],
          ["6+", "Pilot tracks under scoping"],
        ].map(([n, l], i) => (
          <Reveal key={l} delay={i * 60}>
            <div className="bg-[#0A0D14] p-8">
              <div className="font-serif-display text-6xl text-[#D6B25E]">
                {n}
              </div>
              <div className="mt-2 text-[#6F7787] text-sm tracking-[0.18em] uppercase">
                {l}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FounderNote = () => (
  <section
    className="relative bc-light py-20 md:py-28 px-5 sm:px-6 lg:px-10 border-y border-black/10"
    data-testid="about-founder"
  >
    <div className="max-w-3xl mx-auto text-center">
      <Reveal>
        <div className="eyebrow mb-6">06 · Founder’s Note</div>
      </Reveal>
      <Reveal delay={100}>
        <p className="font-serif-display text-3xl md:text-4xl leading-[1.2] italic">
          Believers Common was initiated by its founder as a mission-led
          platform to rebuild trust, organize contribution, and create a safer
          structure for community, work, service, and enterprise participation.
        </p>
      </Reveal>
      <Reveal delay={220}>
        <p className="mt-8 text-[#6F7787] leading-relaxed">
          The founder’s role is not to become the face of the community, but
          to establish the mandate, protect the founding principles, and help
          create the first working systems through which members can
          participate with dignity, accountability, and purpose.
        </p>
      </Reveal>
    </div>
  </section>
);

const CTA = () => (
  <section
    className="relative py-20 md:py-28 px-5 sm:px-6 lg:px-10"
    data-testid="about-cta-section"
  >
    <div className="max-w-[1400px] mx-auto border border-white/10 p-8 sm:p-10 md:p-16 grid grid-cols-12 gap-8 md:gap-10 items-center">
      <div className="col-span-12 md:col-span-8">
        <Reveal>
          <div className="eyebrow mb-4">Join the founding cohort</div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">
            Become part of a{" "}
            <span className="italic text-[#D6B25E]">trusted commons</span>{" "}
            being prepared for community, service, and enterprise.
          </h2>
        </Reveal>
      </div>
      <div className="col-span-12 md:col-span-4 flex md:justify-end">
        <Reveal delay={120}>
          <div className="flex flex-col gap-3">
            <a
              href={buildWhatsAppLink(
                "Hello Believers Common, I'd like to join the founding cohort."
              )}
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
              data-testid="about-whatsapp-cta"
            >
              Message on WhatsApp
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
            </a>
            <Link
              to="/#join"
              className="btn-ghost"
              data-testid="about-form-cta"
            >
              Register your interest
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default About;
